/**
 * Wraps one inheritable section of a follower's editor with a status line:
 * "Master config applies — change anything here to override it for this
 * container only", or "Overridden for this container" with a way back to
 * the master config (re-seeds the section). Nothing is locked: the first
 * edit does the override. The master config's own editor shows, above the
 * section, which containers keep their own copy and "Override N"
 * (OwnersLine, 21/10/2026). One field's source is InheritTag.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, ReactNode } from 'react';
import { Crown, PencilLine, Undo2 } from 'lucide-react';
import type { DropGenSection } from '@trafficpulse/shared';

import { cn } from '@/lib/utils';

import { useMasterPush } from './push/master-push-context';
import { OwnersLine } from './push/markers/OwnersLine';
import { useSectionSources } from './section-sources';

interface Props {
  section: DropGenSection;
  children: ReactNode;
}

export const InheritedSection: FC<Props> = ({ section, children }) => {
  const sources = useSectionSources();
  const owned = useMasterPush().owners(section).length > 0;
  if (!sources.following)
    return owned ? (
      <div className="flex flex-col gap-3">
        <OwnersLine section={section} />
        {children}
      </div>
    ) : (
      <>{children}</>
    );
  const own = sources.isOwn(section);
  const rules = sources.masterRef?.name ?? 'the master config';
  return (
    <div className="flex flex-col gap-3">
      <div
        className={cn(
          'flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]',
          'rounded-md border border-dashed px-3 py-1.5',
          own ? 'border-warning/50 bg-warning/5' : 'border-border/70 bg-muted/20',
        )}
      >
        {own ? (
          <>
            <span className="inline-flex items-center gap-1 font-semibold text-warning">
              <PencilLine className="h-3 w-3" /> Overridden for this container
            </span>
            <span className="text-muted-foreground">— {rules} no longer applies to this section</span>
            <button
              type="button"
              onClick={() => sources.setOwn(section, false)}
              className="inline-flex items-center gap-1 font-medium text-primary hover:underline"
            >
              <Undo2 className="h-3 w-3" /> back to master config
            </button>
          </>
        ) : (
          <>
            <span className="inline-flex items-center gap-1 font-semibold text-foreground">
              <Crown className="h-3 w-3 text-primary" /> Master config
            </span>
            <span className="text-muted-foreground">
              {`from ${rules} — change anything here to override it for this container only`}
            </span>
          </>
        )}
        {sources.masterRef ? (
          <button
            type="button"
            onClick={() => sources.openMaster(sources.masterRef!.id)}
            className="ml-auto inline-flex items-center gap-1 font-medium text-primary hover:underline"
          >
            <Crown className="h-3 w-3" /> edit the master config
          </button>
        ) : null}
      </div>
      {children}
    </div>
  );
};
